import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import numeral from 'numeral';
import { GlobalToken } from 'antd';

import { useStores } from '@/hooks/use-stores';
import { Chip } from '@/comp/index';

import Co2Saving from './Co2Saving';

const Legend = observer(({ shouldRestrictEnergySavings = false }: { shouldRestrictEnergySavings?: boolean }) => {
  const { energySavingsStore } = useStores();
  const { daySavings, monthSavings, yearSavings, min, max } = energySavingsStore.energySavings;
  const { t } = useTranslation();

  return shouldRestrictEnergySavings ? (
    <Container>
      <div className="legend-item legend-item-mtd">
        <div className="legend-item-title">
          <div className="legend-item-bullet" />
          {t('MONTH AVG')}
        </div>
        <div>
          <div className={cn('legend-item-chip', 'calculated-savings-chip')}>
            <Chip
              title={`${numeral(min?.monthSavings?.savingValue).format('0,0')} - ${max?.monthSavings?.savingText}`}
            />
          </div>
          <Co2Saving
            savingValue={`${min?.monthSavings.co2SavingFormatted} - ${max?.monthSavings.co2SavingFormatted}`}
          />
        </div>
      </div>
      <div className="legend-item legend-item-ytd">
        <div className="legend-item-title">
          <div className="legend-item-bullet" />
          {t('YEAR TOTAL')}
        </div>
        <div>
          <div className={cn('legend-item-chip', 'calculated-savings-chip')}>
            <Chip title={`${numeral(min?.yearSavings?.savingValue).format('0,0')} - ${max?.yearSavings?.savingText}`} />
          </div>
          <Co2Saving savingValue={`${min?.yearSavings.co2SavingFormatted} - ${max?.yearSavings.co2SavingFormatted}`} />
        </div>
      </div>
    </Container>
  ) : energySavingsStore.isHistoricalData ? (
    <Container>
      <div className="legend-item legend-item-realtime">
        <div className="legend-item-title">
          <div className="legend-item-bullet" />
          {t('DAY')} <span className="legend-item-power">({daySavings?.energyKwhFormatted} kWh)</span>
        </div>
        <div className="legend-item-savings">
          <span className="legend-item-chip">
            {daySavings?.savingText && <Chip title={daySavings?.savingText ?? ''} />}
          </span>
          {daySavings?.co2SavingFormatted && <Co2Saving savingValue={daySavings?.co2SavingFormatted} />}
        </div>
      </div>
      <div className="legend-item legend-item-mtd">
        <div className="legend-item-title">
          <div className="legend-item-bullet" />
          {t('MONTH')} <span className="legend-item-power">({monthSavings?.energyKwhFormatted} kWh)</span>
        </div>
        <div className="legend-item-savings">
          <span className="legend-item-chip">
            {monthSavings?.savingText && <Chip title={monthSavings?.savingText} />}
          </span>
          {monthSavings?.co2SavingFormatted && <Co2Saving savingValue={monthSavings?.co2SavingFormatted} />}
        </div>
      </div>
      <div className="legend-item legend-item-ytd">
        <div className="legend-item-title">
          <div className="legend-item-bullet" />
          {t('YEAR')} <span className="legend-item-power">({yearSavings?.energyKwhFormatted} kWh)</span>
        </div>
        <div className="legend-item-savings">
          <span className="legend-item-chip">
            {yearSavings?.savingText && <Chip title={yearSavings?.savingText} />}
          </span>
          {yearSavings?.co2SavingFormatted && <Co2Saving savingValue={yearSavings?.co2SavingFormatted} />}
        </div>
      </div>
    </Container>
  ) : (
    <Container>
      <div className="legend-item legend-item-realtime">
        <div className="legend-item-title">
          <div className="legend-item-bullet" />
          {t('DAY')}
          <span className="legend-item-percentage">{daySavings?.percentageFormatted}</span>
          <span className="legend-item-power">({daySavings?.energyKwhFormatted} kWh)</span>
        </div>
      </div>
      <div className="legend-item legend-item-mtd">
        <div className="legend-item-title">
          <div className="legend-item-bullet" />
          {t('MTD')}
          <span className="legend-item-percentage">{monthSavings?.percentageFormatted}</span>
          <span className="legend-item-power">({monthSavings?.energyKwhFormatted} kWh)</span>
        </div>
        <div className="legend-item-savings">
          <span className="legend-item-chip">
            {monthSavings?.savingText && <Chip title={monthSavings?.savingText} />}
          </span>
          {monthSavings?.co2SavingFormatted && <Co2Saving savingValue={monthSavings?.co2SavingFormatted} />}
        </div>
      </div>
      <div className="legend-item legend-item-ytd">
        <div className="legend-item-title">
          <div className="legend-item-bullet" />
          {t('YTD')}
          <span className="legend-item-percentage">{yearSavings?.percentageFormatted}</span>
          <span className="legend-item-power">({yearSavings?.energyKwhFormatted} kWh)</span>
        </div>
        <div className="legend-item-savings">
          <span className="legend-item-chip">
            {yearSavings?.savingText && <Chip title={yearSavings?.savingText} />}
          </span>
          {yearSavings?.co2SavingFormatted && <Co2Saving savingValue={yearSavings?.co2SavingFormatted} />}
        </div>
      </div>
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  display: grid;
  grid-row-gap: 25px;

  .legend-item {
    display: grid;
    grid-row-gap: 5px;
    position: relative;
    color: ${({ theme: { token } }) => token.colorTextSecondary};

    &.legend-item-ytd {
      .legend-item-bullet {
        background-color: ${({ theme: { token } }) => token.Ecoplant_Charts.colorEnergyYear};
      }
    }

    &.legend-item-mtd {
      .legend-item-bullet {
        background-color: ${({ theme: { token } }) => token.Ecoplant_Charts.colorEnergyMonth};
      }
    }

    &.legend-item-realtime {
      .legend-item-bullet {
        background-color: ${({ theme: { token } }) => token.Ecoplant_Charts.colorEnergyDay};
      }
    }

    .legend-item-bullet {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin-right: ${({ theme: { token } }) => token.marginXS}px;
    }

    .legend-item-title {
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      margin-bottom: ${({ theme: { token } }) => token.marginXXS}px;
      display: flex;
      align-items: baseline;
    }

    .legend-item-percentage {
      font-size: ${({ theme: { token } }) => token.fontSizeXL}px;
      color: ${({ theme: { token } }) => token.colorText};
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      margin-left: ${({ theme: { token } }) => token.marginXXS}px;
    }

    .legend-item-power {
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
      margin-left: ${({ theme: { token } }) => token.marginXXS}px;
      font-weight: normal;
    }

    .legend-item-chip {
      padding: 0 ${({ theme: { token } }) => token.paddingXS}px 0 ${({ theme: { token } }) => token.paddingSM}px;
    }

    .calculated-savings-chip {
      margin-bottom: ${({ theme: { token } }) => token.marginXS}px;
    }

    .legend-item-savings {
      display: flex;
      align-items: center;
    }
  }
`;

export default Legend;
